/**
 * ONE function that decides whether a tool call needs a human, for every Meridian surface.
 *
 * ## What changed, and what deliberately did not
 *
 * AI SDK v7 deprecates per-tool `needsApproval` ("Tool approval is handled on a `generateText` /
 * `streamText` level now") in favour of a `toolApproval` policy on the generation call. The 102
 * Hub tool files that declare `needsApproval: true` are NOT touched: they stay the DECLARATION of
 * which tools are dangerous. This module is the single place that READS that declaration and turns
 * it into a decision.
 *
 * That split is the point of #2452. The approval boundary used to be an emergent property of three
 * unrelated mechanisms — a boolean scattered across 102 files, the absence of `execute` on a HITL
 * tool, and an executor splice in `select-agent-tools` — and "what actually prompts?" could only be
 * answered by reading all three. Now it is one function, and a request-specific policy (a stricter
 * gate for a Slack channel with externals in it, say) has somewhere to live.
 *
 * ## The precedence rule this is built on — verified against the SDK, not assumed
 *
 * `resolveToolApproval` in `ai@7.0.87` consults, in order:
 *
 *   1. `toolApproval` when it is a FUNCTION → sole authority, returns immediately;
 *   2. `toolApproval` as a record, if it has an entry for this tool;
 *   3. the tool's own `needsApproval`.
 *
 * Because we pass a function, step 3 NEVER RUNS. Every `needsApproval: true` in the repo stops
 * being load-bearing at the moment this is wired, and this function is the only thing standing
 * between a destructive tool and an unprompted execution. So it reproduces step 3's logic exactly
 * rather than improving on it, and `tests/lib/approval/tool-approval-policy.test.ts` asserts the
 * equivalence tool by tool.
 *
 * ## Prompt caching is unaffected
 *
 * `toolApproval` is a parameter of the generation call, not part of a tool's definition.
 * `needsApproval` was never serialised to the model either. Tool NAMES, DESCRIPTIONS and INPUT
 * SCHEMAS — the cached prefix — are byte-identical before and after (`MERIDIAN_PROMPT_CACHING`).
 */

import type { ToolApprovalStatus } from 'ai';

import { logger } from '../logger.js';

/**
 * The slice of the SDK's approval callback argument this policy reads.
 *
 * Deliberately narrower than `GenericToolApprovalFunction`'s parameter: the SDK passes a superset,
 * which is assignable to this, and naming only what is used keeps the policy testable without
 * constructing a whole typed tool set.
 */
export interface ToolApprovalDecisionInput {
  readonly toolCall: { readonly toolName: string };
  /** The tool map the model was given. Read from HERE, not from a closure, so it cannot go stale. */
  readonly tools?: Record<string, unknown> | undefined;
}

export type ToolApprovalPolicy = (input: ToolApprovalDecisionInput) => ToolApprovalStatus;

/** Read a tool's declared `needsApproval` without assuming the tool's shape. */
function declaredNeedsApproval(tool: unknown): unknown {
  if (typeof tool !== 'object' || tool === null) return undefined;
  return (tool as { needsApproval?: unknown }).needsApproval;
}

/**
 * The policy for a STREAMING turn (Hub `/chat`, Slack, routines, background turns — everything that
 * goes through `streamBedrockResponse`).
 *
 * Equivalent to the SDK's own per-tool fallback, with one deliberate difference: a `needsApproval`
 * declared as a FUNCTION resolves to `user-approval` instead of being invoked.
 *
 * That difference is safe in one direction only, which is why it is the direction chosen. The
 * dynamic form needs a validated tool context the SDK builds internally and does not expose, so
 * calling it correctly from out here is not possible; guessing would risk approving something that
 * should have prompted. No tool in this repo uses the dynamic form — 102 declare a literal `true`
 * and none declares `false` or a function — so today this branch is unreachable, and if one ever
 * appears the failure is an extra prompt rather than a silent mutation.
 */
export function streamToolApprovalPolicy(input: ToolApprovalDecisionInput): ToolApprovalStatus {
  const declared = declaredNeedsApproval(input.tools?.[input.toolCall.toolName]);

  // Matches the SDK exactly: an ABSENT declaration means the tool is not approval-gated. Note this
  // is `== null`, so an explicit `false` falls through to the truthiness check below and correctly
  // yields `not-applicable` — the same answer, by the same route.
  if (declared === undefined || declared === null) {
    return 'not-applicable';
  }

  if (typeof declared === 'function') {
    logger.warn('tool declares a dynamic needsApproval; requiring approval conservatively', {
      op: 'streamToolApprovalPolicy',
      tool: input.toolCall.toolName,
    });
    return 'user-approval';
  }

  return declared ? 'user-approval' : 'not-applicable';
}

/**
 * The policy INSIDE a nested agent loop: nothing ever prompts.
 *
 * This is not a relaxation, it is the existing contract made explicit. A write agent is reached
 * only through `delegate_task`, which is itself HITL-gated — the user approved the delegation, and
 * that one approval covers the writes inside it (`MERIDIAN_AGENT_DELEGATION`). A read agent cannot
 * hold a write tool at all, because `select-agent-tools` splices approval executors for
 * `mode: 'write'` ONLY and a HITL tool has no other callable form.
 *
 * Stating it here closes a real gap rather than restating a safe one. The nested loop has NO HITL
 * channel, so a tool that still asked for approval would suspend the agent until its budget
 * expired — a hang, presented to the user as a timeout. Previously nothing prevented that except
 * the coincidence that HITL tools carry no `execute`; a tool that ever carried both would have
 * stalled every agent that named it.
 *
 * The approval boundary itself is unchanged and still lives where it did: the preset allow-list
 * runs first, the forbidden deny-list second, and sends/publishes/deletes/merges are never
 * delegable. This function decides PROMPTING, not REACHABILITY, and it cannot widen the tool set.
 */
export function agentToolApprovalPolicy(): ToolApprovalStatus {
  return 'not-applicable';
}
